import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useToast } from '../../../../hooks/use-toast';
import { activateCouponForSeller } from '../../../../services/couponAdminApi';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../components/ui/card';
import withAdminActionLogger from '../../../../hocs/withAdminActionLogger';

const ActivateCouponSection = ({ sellerProfileId, logAction, onSuccess }) => {
    const { toast } = useToast();
    const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm();
    const [activationResult, setActivationResult] = useState(null);

    const onSubmit = async (data) => {
        setActivationResult(null);
        try {
            const res = await activateCouponForSeller(sellerProfileId, data.couponCode);
            setActivationResult({ success: true, message: res.data.message });
            toast({ title: 'Success!', description: res.data.message });
            logAction("COUPON_ACTIVATE", {
                target_entity: "SELLER_PROFILE",
                target_entity_id: sellerProfileId,
                details: `Activated coupon code '${data.couponCode}' for seller.`
            });
            reset();
            onSuccess?.();
        } catch (err) {
            const errorMsg = err.response?.data?.detail || 'Failed to activate coupon.';
            setActivationResult({ success: false, message: errorMsg });
            toast({ variant: 'destructive', title: 'Activation Failed', description: errorMsg });
        }
    };

    return (
        <Card className="border-none shadow-none">
            <CardHeader>
                <CardTitle>Activate a Coupon</CardTitle>
                <CardDescription>
                    Manually activate a pre-authorized coupon for this seller. The coupon must be assigned to your user group.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div>
                        <Label htmlFor="couponCode">Coupon Code</Label>
                        <Input
                            id="couponCode"
                            placeholder="Enter coupon code"
                            {...register('couponCode', { required: 'Coupon code is required.' })}
                        />
                        {errors.couponCode && <p className="text-sm text-destructive mt-1">{errors.couponCode.message}</p>}
                    </div>
                    <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Activating...' : 'Activate Coupon'}
                    </Button>
                </form>
                {activationResult && (
                    <div className={`mt-4 p-3 rounded-md text-sm ${activationResult.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {activationResult.message}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default withAdminActionLogger(ActivateCouponSection);
